import { useMemo } from "react";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { refreshAll } from "./queries";

// The three next/navigation hooks the ported components use, over TanStack
// Router. Hrefs stay plain strings ("/sales?from=...&to=...") so the
// components read as they did; parseHref() turns them into a navigation.

export type ParsedHref = { to: string; search: Record<string, string>; hash?: string };

export function parseHref(href: string): ParsedHref {
  const hashAt = href.indexOf("#");
  const hash = hashAt === -1 ? undefined : href.slice(hashAt + 1);
  const noHash = hashAt === -1 ? href : href.slice(0, hashAt);
  const q = noHash.indexOf("?");
  const to = q === -1 ? noHash : noHash.slice(0, q);
  const search = q === -1 ? {} : Object.fromEntries(new URLSearchParams(noHash.slice(q + 1)));
  return { to: to || "/", search, hash };
}

export function usePathname(): string {
  return useLocation({ select: (l) => l.pathname });
}

export function useSearchParams(): URLSearchParams {
  const searchStr = useLocation({ select: (l) => l.searchStr });
  return useMemo(() => new URLSearchParams(searchStr), [searchStr]);
}

export function useRouter() {
  const navigate = useNavigate();
  return useMemo(
    () => ({
      push: (href: string) => {
        const { to, search, hash } = parseHref(href);
        void navigate({ to: to as never, search: search as never, hash });
      },
      replace: (href: string) => {
        const { to, search, hash } = parseHref(href);
        void navigate({ to: to as never, search: search as never, hash, replace: true });
      },
      /** Re-read every query (what Next's router.refresh() did for the server data). */
      refresh: () => {
        void refreshAll();
      },
    }),
    [navigate],
  );
}
